import classNames from "classnames"
import React, { ReactNode, useMemo } from "react"

import { Experiment } from "../../../../constants/experiments"
import { Color } from "../../../../constants/V2/color"
import { useIsExperiment } from "../../../../hooks/experiments"
import { useIsBreakpoint } from "../../../../utils/V2/screen"
import { useGetStartedModalStore } from "../../../V2/Sections/GetStarted/store"
import Link from "../../Link"
import ArrowIcon from "../Icons/ArrowIcon"
import Typography, { TypographySize } from "../Typography"

import {
  SharedButtonClickProps,
  handleOnClick,
} from "@utils/buttonEventHandler"
import {
  getBorderColorClass,
  getBackgroundColorClass,
  getBackgroundHoverColorClass,
  getAccentColor,
  getTextColorClass,
  getTextHoverColorClass,
} from "@utils/V2/color"

type Props = {
  style: "solid" | "bordered"
  size: "large" | "medium" | "small"
  type?: "button" | "submit"
  color: Color
  overrideTextColor?: Color
  text: string
  fontSize?: TypographySize
  mobileText?: string
  linkUrl?: string
  queryParams?: string
  mobileLinkUrl?: string
  onClick?: () => void
  transparentHover?: boolean
  hideIcon?: boolean
  icon?: ReactNode
  iconPosition?: "left" | "right"
  disabled?: boolean
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
  className?: string
  secondaryText?: string
  hasScrollArrow?: boolean
  "data-testid"?: string
}

const PillButton = ({
  style,
  size = "large",
  type = "button",
  color,
  overrideTextColor = getAccentColor(color),
  text,
  mobileText,
  fontSize,
  linkUrl,
  queryParams,
  mobileLinkUrl,
  onClick,
  transparentHover = false,
  hideIcon = false,
  icon = <ArrowIcon />,
  iconPosition = "right",
  disabled = false,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  className,
  secondaryText,
  hasScrollArrow = false,
  "data-testid": dataTestId,
}: Props) => {
  const isMobile = useIsBreakpoint("mobile")
  const isTablet = useIsBreakpoint("tablet")
  const sizeOverride = isMobile || isTablet ? "medium" : size

  const isGetStartedModalExperiment = useIsExperiment(
    Experiment.GET_STARTED_MODAL
  )
  const { setIsGetStartedModalOpen } = useGetStartedModalStore()

  const showGetStartedModal =
    isGetStartedModalExperiment && text.toLowerCase() === "get started"

  const sharedButtonClickProps: SharedButtonClickProps = {
    trackingEvent,
    trackingEventKey,
    trackingEventValue,
    onClick,
  }

  const buttonClasses = useMemo(
    () =>
      classNames(
        getBorderColorClass(color),
        style === "solid"
          ? getBackgroundColorClass(color)
          : [
              "bg-transparent transition-colors duration-300",
              getBackgroundHoverColorClass(color),
              transparentHover && "hover:bg-opacity-10",
            ],
        { "opacity-50 pointer-events-none": disabled },
        "group rounded-32 border flex gap-10 items-center w-full sm:w-max justify-center",
        {
          "px-20 py-[15px] md:px-[18px] md:py-[13px] lg:px-20 lg:py-[15px]": [
            "large",
            "medium",
          ].includes(sizeOverride),
        },
        {
          "px-16 py-[9px]": size === "small",
        },
        { "flex-row-reverse": iconPosition === "left" },
        className
      ),
    [
      color,
      style,
      transparentHover,
      disabled,
      sizeOverride,
      size,
      iconPosition,
      className,
    ]
  )

  const buttonContentClasses = classNames(
    "!leading-none transition-colors duration-300",
    style === "bordered" &&
      !transparentHover &&
      getTextHoverColorClass(getAccentColor(color))
  )

  const buttonContent = (
    <>
      <div className="flex gap-10 pt-[2px]">
        {mobileText ? (
          <Typography
            text={isMobile ? mobileText : text}
            weight="medium"
            font="grotesk"
            color={overrideTextColor}
            size={
              fontSize || (size === "large" ? "body-lg-dual" : "body-md-fixed")
            }
            className={buttonContentClasses}
          />
        ) : (
          <Typography
            text={text}
            weight="medium"
            font="grotesk"
            color={overrideTextColor}
            size={
              fontSize || (size === "large" ? "body-lg-dual" : "body-md-fixed")
            }
            className={buttonContentClasses}
          />
        )}

        {secondaryText && (
          <Typography
            text={secondaryText}
            weight="medium"
            font="grotesk"
            color={overrideTextColor}
            size={
              fontSize || (size === "large" ? "body-lg-dual" : "body-md-fixed")
            }
            className={buttonContentClasses}
          />
        )}
      </div>
      {!hideIcon && (
        <div
          className={classNames(
            getTextColorClass(overrideTextColor),
            { "rotate-90 pl-[2px]": hasScrollArrow },
            hasScrollArrow && size === "small"
              ? "w-16 !pl-0"
              : "w-20 md:w-[22px] lg:w-24",
            buttonContentClasses
          )}
        >
          {icon}
        </div>
      )}
    </>
  )

  if (showGetStartedModal) {
    return (
      <button
        type={type}
        onClick={(e) =>
          handleOnClick({
            e,
            ...sharedButtonClickProps,
            onClick: () => setIsGetStartedModalOpen(true),
          })
        }
        className={buttonClasses}
        disabled={disabled}
        data-testid={dataTestId}
      >
        {buttonContent}
      </button>
    )
  }

  if (linkUrl && mobileLinkUrl) {
    return (
      <Link
        link={isMobile ? mobileLinkUrl : linkUrl}
        onClick={(e) =>
          handleOnClick({
            e,
            linkUrl: isMobile ? mobileLinkUrl : linkUrl,
            ...sharedButtonClickProps,
          })
        }
        className={buttonClasses}
        queryParams={queryParams}
      >
        {buttonContent}
      </Link>
    )
  } else if (linkUrl && !mobileLinkUrl) {
    return (
      <Link
        link={linkUrl}
        onClick={(e) =>
          handleOnClick({ e, linkUrl, ...sharedButtonClickProps })
        }
        className={buttonClasses}
        queryParams={queryParams}
      >
        {buttonContent}
      </Link>
    )
  }

  return (
    <button
      type={type}
      onClick={(e) => handleOnClick({ e, ...sharedButtonClickProps })}
      className={buttonClasses}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {buttonContent}
    </button>
  )
}

export default PillButton
